import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { useDocuments } from 'contexts/documents'
import { useWithinQuota } from 'hooks/within-quota'
import { omit, size } from 'utilities/object'
import { pruneBy } from 'utilities/array'
import { getListRepresentationFromProfile } from 'utilities/person'
import { Heading } from './s'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import TimeField from 'components/form/field/time'
import SelectField from 'components/form/field/select'
import TextField from 'components/form/field/text'
import PeopleField from 'components/form/field/people'
import QuickSelect from 'components/form/quick-select'
import ExternalSignatories from './external-signatories'
import Paragraph from 'components/typography/paragraph'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import Loader from 'components/loader'
import SigningQuota from 'pages/documents/modals/signing-quota'

const ConfigureDocumentSigning = ({ dokument, onDone, salt }) => {
    const { formatMessage } = useIntl()
    const { scriveLocales } = useI18n()

    const {
        addSigning,
        restartSigning
    } = useDocuments()

    const { signing } = dokument
    const { status } = signing ?? {}

    const initialInternalSignatories = !!signing ?
        signing.signatories
            .filter(({ user }) => !!user?.id)
            .map(({ user }) => ({
                ...user,
                type: 'user'
            })) :
        pruneBy([
            ...dokument.owners.filter(({ type }) => type === 'user'),
            ...dokument.shares.filter(({ type }) => type === 'user')
        ])

    const [internalSignatories, setInternalSignatories] = useState(initialInternalSignatories)
    const [externalSignatories, setExternalSignatories] = useState([])
    const [whistle, setWhistle] = useState(Date.now())
    const [sending, setSending] = useState(false)

    const sendOrRestart = async body => {
        const signatories = [
            ...body.internalSignatories.map(signatory => ({
                ...signatory,
                type: 'internal'
            })),
            ...(externalSignatories?.map(signatory => ({
                ...signatory,
                type: 'external'
            })) ?? [])
        ]

        body = {
            ...omit(body, 'internalSignatories'),
            signatories
        }

        const addOrRestartSigning = status ?
            restartSigning :
            addSigning

        setSending(true)
        const { ok } = await addOrRestartSigning(body, dokument.id)
        setSending(false)

        ok && onDone?.()
    }

    useEffect(() => {
        if(signing) {
            const externalSignatories = signing.signatories
                .filter(({ user }) => !!user && !user?.id)
                .map(({ user }) => user)

            setExternalSignatories(externalSignatories)
        }
    }, [])

    return (
        <Form
            layout="vertical"
            onSubmit={sendOrRestart}>
            {({ touched, errors, trigger }) => (
                <>
                    <Heading>
                        <FormattedMessage
                            id={status ?
                                'document_signing_action_restart' :
                                'document_action_send_for_signing'
                            }
                            defaultMessage={status ?
                                'Restart signing ' :
                                'Send for signing'
                            } />
                    </Heading>
                    <StringField
                        salt={salt}
                        label={formatMessage({
                            id: 'document_label_name',
                            defaultMessage: 'Name of document'
                        })}
                        name="name"
                        field={{
                            value: dokument.name,
                            include: 'always',
                            editable: false
                        }}
                        view={{ ellipsify: { tail: 9 } }} />
                    <TimeField
                        salt={salt}
                        label={formatMessage({
                            id: 'noun_due_date',
                            defaultMessage: 'Due date'
                        })}
                        name="dueAt"
                        field={{
                            value: signing?.dueAt ?? null,
                            required: true,
                            include: 'always'
                        }}
                        picker={{
                            past: false,
                            blockedDateRanges: [{ on: new Date() }]
                        }} />
                    <SelectField
                        salt={salt}
                        label={formatMessage({
                            id: 'document_signing_label_language',
                            defaultMessage: 'Language'
                        })}
                        name="language"
                        field={{
                            value: signing?.dueAt ?? null,
                            required: true,
                            include: 'always',
                            allowEmptyOption: false,
                            optional: formatMessage({
                                id: 'document_signing_label_language_tooltip',
                                defaultMessage: 'Controls the language of both the emails sent to signers, and the resulting verification added to the PDF.'
                            })
                        }}
                        options={scriveLocales.map(({ locale, name }) => ({
                            key: locale,
                            value: locale,
                            text: name
                        }))} />
                    <TextField
                        salt={salt}
                        label={formatMessage({
                            id: 'document_label_invitation_message',
                            defaultMessage: 'Invitiation message'
                        })}
                        name="invitationMessage"
                        field={{
                            value: signing?.invitationMessage ?? null,
                            include: signing ? 'touched' : 'always'
                        }}
                        controlProps={{
                            placeholder: formatMessage({
                                id: 'document_placeholder_invitation_message',
                                defaultMessage: 'Add a message to the email being sent out for signing.'
                            })
                        }} />
                    <TextField
                        salt={salt}
                        label={formatMessage({
                            id: 'document_label_confirmation_message',
                            defaultMessage: 'Confirmation message'
                        })}
                        name="confirmationMessage"
                        field={{
                            value: signing?.confirmationMessage ?? null,
                            include: signing ? 'touched' : 'always'
                        }}
                        controlProps={{
                            placeholder: formatMessage({
                                id: 'document_placeholder_confirmation_message',
                                defaultMessage: 'Add a message to the email being sent when the document has been signed.'
                            })
                        }} />
                    <PeopleField
                        salt={salt}
                        label={formatMessage({
                            id: 'document_label_internal_signatories',
                            defaultMessage: 'Signatories in Huma'
                        })}
                        name="internalSignatories"
                        field={{
                            value: internalSignatories,
                            include: 'always',
                            displayAs: 'names'
                        }}
                        picker={{ outer: false }}
                        onChange={({ internalSignatories }) => setInternalSignatories(internalSignatories)}
                        whistle={whistle} />
                    <QuickSelect
                        options={[
                            ...initialInternalSignatories,
                            !!dokument.concerns && getListRepresentationFromProfile(dokument.concerns)
                        ]}
                        values={internalSignatories}
                        insertValue={value => {
                            setInternalSignatories(internalSignatories => [
                                ...internalSignatories,
                                value
                            ])

                            setWhistle(Date.now())
                        }} />
                    <ExternalSignatories
                        externalSignatories={externalSignatories}
                        setExternalSignatories={setExternalSignatories}
                        salt={salt} />
                    <Paragraph className="compact">
                        <FormattedMessage
                            id="documents_message_send_for_signing"
                            defaultMessage="An email will be sent out to all signatories. You and those with edit access to this document will see the signing progress." />
                    </Paragraph>
                    <Actions>
                        <Plain
                            onClick={onDone}
                            className="neutral"
                            disabled={sending}>
                            <FormattedMessage
                                id="action_cancel"
                                defaultMessage="Cancel" />
                        </Plain>
                        <ButtonSubmit
                            className={`constructive${sending ? ' loading' : ''}`}
                            disabled={(!touched.length && !signing) || !!size(errors) || sending}
                            ref={trigger}>
                            <FormattedMessage
                                id="document_action_send_for_signing"
                                defaultMessage="Send for signing" />
                        </ButtonSubmit>
                    </Actions>
                </>
            )}
        </Form>
    )
}

export default props => {
    const [withinSigningQuota,, state] = useWithinQuota({
        prefix: 'feature',
        key: 'digital-signing'
    })

    const { checking = false } = state

    if(checking) {
        return <Loader />
    }

    if(withinSigningQuota) {
        return <ConfigureDocumentSigning {...props} />
    }

    return <SigningQuota {...props} />
}
